<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-base font-semibold text-gray-900">Notification Preferences</h2>
      <p class="text-sm text-gray-500 mt-0.5">Configure which notifications and alerts are enabled</p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 py-16 text-center">
      <i class="fa-solid fa-spinner fa-spin text-gray-400 text-xl mb-3"></i>
      <p class="text-sm text-gray-400">Loading preferences...</p>
    </div>

    <template v-else>
      <div class="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        <div v-for="pref in preferences" :key="pref.key"
          class="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
          @click="togglePref(pref.key)">
          <div>
            <p class="text-sm font-medium text-gray-900 text-left">{{ pref.label }}</p>
            <p class="text-xs text-gray-400 mt-0.5 text-left">{{ pref.description }}</p>
          </div>
          <span
            class="relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-200 cursor-pointer"
            :class="form[pref.key] ? 'bg-primary' : 'bg-gray-300'"
            style="width:44px;height:24px">
            <span
              class="inline-block bg-white rounded-full shadow transition-transform duration-200"
              style="width:20px;height:20px"
              :style="{ transform: form[pref.key] ? 'translateX(22px)' : 'translateX(2px)' }"></span>
          </span>
        </div>
      </div>

      <!-- Save -->
      <div class="flex justify-end">
        <button @click="save()" :disabled="saving"
          class="inline-flex items-center px-5 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors disabled:opacity-50"
          style="height:40px">
          <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

      <div v-if="saved" class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
        <i class="fa-solid fa-circle-check text-green-600"></i>
        <span class="text-sm text-green-700 font-medium">Notification preferences saved</span>
      </div>
    </template>
  </div>
</template>

<script>
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";

const API_BASE = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      saved: false,
      form: {
        forwardChangesToOffice: true,
        forwardUnmatchedSms: true,
        notifyNewJobRequests: true,
        notifyCancellations: true,
        notifyChangeRequests: true,
        notifyComplaints: true,
      },
      preferences: [
        { key: 'forwardChangesToOffice', label: 'Forward Changes to Office', description: 'Forward job change requests to the office phone number' },
        { key: 'forwardUnmatchedSms', label: 'Forward Unmatched SMS', description: 'Forward SMS that cannot be matched to a job to the office number' },
        { key: 'notifyNewJobRequests', label: 'New Job Requests', description: 'Get notified when clients request new jobs via SMS' },
        { key: 'notifyCancellations', label: 'Cancellations', description: 'Get notified when clients cancel jobs via SMS' },
        { key: 'notifyChangeRequests', label: 'Change Requests', description: 'Get notified when clients request job changes via SMS' },
        { key: 'notifyComplaints', label: 'Complaints', description: 'Get notified when clients send complaints via SMS' },
      ],
    };
  },
  methods: {
    togglePref(key) {
      this.form[key] = !this.form[key];
    },
    getCurrentUser() {
      return new Promise((resolve, reject) => {
        if (auth.currentUser) return resolve(auth.currentUser);
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          user ? resolve(user) : reject(new Error('Not authenticated'));
        });
      });
    },
    async getHeaders() {
      const user = await this.getCurrentUser();
      const token = await user.getIdToken();
      return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    },
    async fetchData() {
      this.loading = true;
      try {
        const headers = await this.getHeaders();
        const res = await fetch(`${API_BASE}/notifications`, { headers });
        const json = await res.json();
        const data = json.data || {};
        for (const key of Object.keys(this.form)) {
          if (data[key] !== undefined) this.form[key] = data[key];
        }
      } catch (e) {
        console.error('Failed to load notification prefs:', e);
      }
      this.loading = false;
    },
    async save() {
      this.saving = true;
      this.saved = false;
      try {
        const headers = await this.getHeaders();
        await fetch(`${API_BASE}/notifications`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(this.form),
        });
        this.saved = true;
        setTimeout(() => { this.saved = false; }, 3000);
      } catch (e) {
        console.error('Failed to save notification prefs:', e);
      }
      this.saving = false;
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
